import { ErrorMessage } from "@hookform/error-message";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useState, type RefCallback } from "react";
import {
  Controller,
  FieldError,
  FieldErrors,
  useFormContext,
} from "react-hook-form";
import CustomErrorMessage from "@/components/forms/inputsWithRef/customErrorMessage";
import dayjs, { Dayjs } from "dayjs";
import styles from "@/components/forms/inputs/genericDatePicker.module.css";
import { FormControlLabel } from "@mui/material";
import classNames from "classnames";
import GenericDateRange from "@/components/dateTime/genericDateRange";

interface Props {
  name: string;
  label: string | any;
  predefinedvalue?: any;
  disabled?: boolean;
  hideError?: boolean;
  labelClassname?: any;
  inputClassname?: any;
  useInternalController?: boolean;

  //external Controller
  value?: Dayjs | null;
  externalOnChange?: (value: any) => void;
  error?: FieldError;
  errors?: FieldErrors;
  validationTargetRef?: RefCallback<HTMLElement>;
}

const GenericDatePicker: FC<Props> = ({
  name,
  label,
  predefinedvalue,
  disabled,
  labelClassname,
  inputClassname,
  hideError,
  useInternalController = true,

  value,
  externalOnChange,
  error,
  errors,
  validationTargetRef,
}) => {
  const { setValue, control, trigger } = useFormContext();
  const { t } = useTranslation();

  const [dateValue, setDateValue] = useState<any>(null);

  useEffect(() => {
    if (predefinedvalue) {
      const date = dayjs(predefinedvalue);
      setDateValue(date);
      setValue(name, predefinedvalue);
    }
  }, [predefinedvalue, setValue, name]);

  const onChangeHandler = (newDate: any, changeFn?: any) => {
    if (newDate) {
      const tempDate = dayjs(newDate.$d).format("DD.MM.YYYY");
      changeFn(tempDate);
    } else {
      changeFn(null);
    }
  };

  const handleClearDate = () => {
    setDateValue(null);
    setValue(name, "");
    trigger(name);
  };

  const renderDatePicker = ({
    value,
    onChange,
    error,
    errors,
  }: {
    value: Dayjs | null;
    onChange?: (value: any) => void;
    error?: FieldError;
    errors?: FieldErrors;
  }) => (
    <div ref={validationTargetRef}>
      <FormControlLabel
        labelPlacement="top"
        sx={{
          margin: "0",
          width: "100%",
          alignItems: "start",
          fontFamily: "inherit !important",
        }}
        disableTypography
        label={
          <section
            className={classNames(styles.label, {
              [labelClassname]: labelClassname,
            })}
          >
            {label}
          </section>
        }
        control={
          <GenericDateRange
            selectionMode="single"
            value={value}
            disabled={disabled}
            onChange={(newValue) => {
              onChangeHandler(newValue, onChange);
              setDateValue(newValue);
            }}
            onClear={handleClearDate}
            textFieldProps={{
              name: t("date"),
              error: !!error,
              className: styles.fullWidth,
              InputProps: {
                classes: {
                  root: styles.outlinedInputRounded,
                  input: inputClassname,
                },
              },
            }}
          />
        }
      />

      {!hideError && errors && (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <CustomErrorMessage error="error" errorMessage={message} />
          )}
        />
      )}
    </div>
  );

  if (!useInternalController) {
    return renderDatePicker({
      value: value ?? dateValue,
      onChange: externalOnChange,
      error,
      errors,
    });
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: t("field_required"),
      }}
      render={({
        field: { onChange },
        fieldState: { error },
        formState: { errors },
      }) =>
        renderDatePicker({
          value: dateValue,
          onChange,
          error,
          errors,
        })
      }
    />
  );
};

export default GenericDatePicker;
